import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PanelState, { extractApiError } from "@/components/PanelState";
import { ChevronLeft, ChevronRight, ChevronDown, Trophy, Target } from "lucide-react";
import { WallMonthlyPresentation } from "./PresentationViews";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmt$c = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  ON_TARGET: "bg-[#dcfce7] text-[#166534] border-[#4ade80] font-bold",
  WATCH:     "bg-[#fef3c7] text-[#854d0e] border-[#facc15] font-bold",
  OFF_TRACK: "bg-[#fee2e2] text-[#991b1b] border-[#f87171] font-bold",
  NO_GOAL:   "bg-[#e5e7eb] text-[#4b5563] border-[#9ca3af] font-bold",
};

function GoalBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.NO_GOAL;
  const label = status === "NO_GOAL" ? "NO GOAL SET" : status.replace(/_/g, " ");
  return (
    <span className={`inline-flex text-[10px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 ${cls}`}>
      {label}
    </span>
  );
}

// Monthly goal cell: shows the derived-from-weekly sum plus an inline override
// input. Leaving the input blank restores the derived value.
function MonthlyGoalInput({ techName, monthlyGoal, derived, hasOverride, month, canEdit, onSaved }) {
  const [val, setVal] = useState(hasOverride ? String(monthlyGoal) : "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { setVal(hasOverride ? String(monthlyGoal) : ""); }, [monthlyGoal, hasOverride, month]);

  const commit = async () => {
    setErr(null);
    const trimmed = val.trim();
    const original = hasOverride ? String(monthlyGoal) : "";
    if (trimmed === original) return;
    const payload = trimmed === "" ? null : Number(trimmed);
    if (trimmed !== "" && (Number.isNaN(payload) || payload < 0)) {
      setErr("Must be a positive number");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/tech-monthly-goals", { tech_name: techName, month, monthly_goal: payload });
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const placeholder = derived != null ? `Auto: ${fmt$(derived)}` : "Set goal";

  return (
    <div className="flex items-center gap-1.5 justify-end">
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] font-mono-data text-[color:var(--cc-ink-muted)]">$</span>
        <input
          type="text"
          inputMode="numeric"
          disabled={!canEdit || saving}
          value={val}
          placeholder={placeholder}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          data-testid={`monthly-goal-input-${techName}`}
          className={`pl-5 pr-2 py-1 w-32 text-[12px] font-mono-data text-right border rounded ${
            hasOverride ? "border-[#0072bc] bg-[#f0f7fb]" : "border-[#e7e9ee] bg-white"
          } focus:outline-none focus:ring-2 focus:ring-[#0072bc]/40 ${!canEdit ? "opacity-70 cursor-not-allowed" : ""}`}
        />
      </div>
      {hasOverride && (
        <span
          data-testid={`monthly-goal-override-${techName}`}
          className="text-[9px] font-mono-data uppercase tracking-widest text-[#0072bc]"
          title={`Override — auto-sum would be ${fmt$(derived)}`}
        >
          override
        </span>
      )}
      {err && <span className="text-[10px] text-[#991b1b]">{err}</span>}
    </div>
  );
}

export default function WallMonthlyMode({ month, setMonth, availableMonths, currentMonthOf, fullscreen = false }) {
  const { user } = useAuth();
  const canEdit = !!user && ["platform_admin", "owner", "administrative", "gm_ops", "call_center", "dispatch_csr"].includes(user.role);
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = async (m) => {
    try {
      const { data: resp } = await api.get(`/kpi/wall-monthly${m ? `?month=${m}` : ""}`);
      setData(resp);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setData(null);
      setLoadState({ status: "error", error: extractApiError(err) });
    }
  };
  useEffect(() => { load(month); }, [month]);

  const currentIdx = availableMonths.findIndex((m) => m.month === (data?.month || currentMonthOf));
  const canGoOlder = currentIdx >= 0 && currentIdx < availableMonths.length - 1;
  const canGoNewer = currentIdx > 0;
  const goOlder = () => { if (canGoOlder) setMonth(availableMonths[currentIdx + 1].month); };
  const goNewer = () => { if (canGoNewer) setMonth(availableMonths[currentIdx - 1].month); };
  const pickerLabel = data?.month_label || "This Month";

  const isEmpty = loadState.status === "ok" && (!data || !data.techs || data.techs.length === 0);
  if (loadState.status !== "ok" || isEmpty) {
    return (
      <PanelState
        status={loadState.status}
        error={loadState.error}
        isEmpty={isEmpty}
        onRetry={() => load(month)}
        emptyTitle="No monthly data yet"
        emptyBody={<>Switch months or wait for jobs to sync.</>}
        testIdPrefix="kpi-wall-monthly"
      />
    );
  }

  const totals = data.totals;
  const maxRev = Math.max(...data.techs.map((t) => t.revenue), 1);
  const noGoalCount = data.techs.filter((t) => t.monthly_goal == null).length;
  const topTech = data.techs.find((t) => !t.no_jobs);

  // Presentation-mode short-circuit — scoreboard layout for the Monday
  // meeting. Simple arrow-nav on month; dropdown-picker kept for desk view.
  if (fullscreen) {
    return (
      <div>
        <div className="flex justify-end items-center gap-2 mb-4">
          <button type="button" onClick={goNewer} disabled={!canGoNewer} data-testid="pres-month-newer"
            className={`p-3 rounded-lg border-2 ${canGoNewer ? "border-[#0e1420] text-[#0e1420] hover:bg-[#f5f6f8]" : "border-[#e7e9ee] text-[#c9cfd8] cursor-not-allowed"}`}>
            <ChevronLeft size={22} strokeWidth={2.2} />
          </button>
          <div className="px-6 py-3 text-[22px] font-bold text-[#0e1420] bg-white border-2 border-[#0e1420] rounded-lg min-w-[240px] text-center">
            {data.month_label}
          </div>
          <button type="button" onClick={goOlder} disabled={!canGoOlder} data-testid="pres-month-older"
            className={`p-3 rounded-lg border-2 ${canGoOlder ? "border-[#0e1420] text-[#0e1420] hover:bg-[#f5f6f8]" : "border-[#e7e9ee] text-[#c9cfd8] cursor-not-allowed"}`}>
            <ChevronRight size={22} strokeWidth={2.2} />
          </button>
        </div>
        <WallMonthlyPresentation data={data} />
      </div>
    );
  }

  return (
    <div data-testid="kpi-wall-monthly-mode" className="space-y-5">
      {/* Month picker */}
      <div className="flex items-center justify-end gap-2">
        <div
          className="inline-flex items-center gap-1 bg-white rounded-lg p-1 border border-[#e7e9ee] relative"
          data-testid="wall-month-switch"
        >
          <button
            type="button"
            onClick={goNewer}
            disabled={!canGoNewer}
            data-testid="wall-month-newer"
            title="Newer month"
            className={`p-1.5 rounded-md ${canGoNewer ? "hover:bg-[#f5f6f8] text-[#0e1420]" : "text-[#c9cfd8] cursor-not-allowed"}`}
          >
            <ChevronLeft size={14} strokeWidth={2.2} />
          </button>
          <button
            type="button"
            onClick={() => setPickerOpen((o) => !o)}
            data-testid="wall-month-picker"
            className="px-3 py-1.5 text-[11.5px] rounded-md font-semibold text-[#0e1420] hover:bg-[#f5f6f8] inline-flex items-center gap-1.5 min-w-[140px] justify-center"
          >
            <span>{pickerLabel}</span>
            <ChevronDown size={12} strokeWidth={2.2} className="opacity-60" />
          </button>
          <button
            type="button"
            onClick={goOlder}
            disabled={!canGoOlder}
            data-testid="wall-month-older"
            title="Older month"
            className={`p-1.5 rounded-md ${canGoOlder ? "hover:bg-[#f5f6f8] text-[#0e1420]" : "text-[#c9cfd8] cursor-not-allowed"}`}
          >
            <ChevronRight size={14} strokeWidth={2.2} />
          </button>
          {pickerOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setPickerOpen(false)} />
              <div
                data-testid="wall-month-picker-dropdown"
                className="absolute top-full right-0 mt-1 bg-white border border-[#e7e9ee] rounded-lg shadow-lg z-40 min-w-[240px] max-h-[360px] overflow-y-auto"
              >
                {availableMonths.length === 0 && (
                  <div className="px-3 py-2 text-[11.5px] text-[color:var(--cc-ink-muted)] italic">No months available</div>
                )}
                {availableMonths.map((m) => (
                  <button
                    key={m.month}
                    type="button"
                    onClick={() => { setMonth(m.month); setPickerOpen(false); }}
                    data-testid={`wall-month-opt-${m.month}`}
                    className={`w-full text-left px-3 py-2 text-[12px] hover:bg-[#f5f6f8] flex items-center justify-between ${
                      m.month === data.month ? "bg-[#e7f4fb] text-[#0072bc] font-semibold" : "text-[#0e1420]"
                    }`}
                  >
                    <span>
                      {m.label}
                      {m.is_this && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#0072bc]">This</span>}
                    </span>
                    <span className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">{m.job_count} jobs</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Missing-goal callout */}
      {noGoalCount > 0 && (
        <div className="bg-[#fef3c7] border border-[#facc15] rounded-xl p-4 flex items-start gap-3" data-testid="wall-monthly-missing-goals-callout">
          <Target size={18} strokeWidth={1.8} className="text-[#854d0e] mt-0.5" />
          <div className="text-[13px] text-[#854d0e]">
            <b>{noGoalCount} tech{noGoalCount === 1 ? "" : "s"} have no monthly goal.</b>{" "}
            Set weekly goals in Weekly mode and they&apos;ll auto-sum here — or type an override in the input on the right to lock a stretch target.
          </div>
        </div>
      )}

      {/* Monthly totals card */}
      <div
        className="bg-white border border-[#e7e9ee] rounded-xl p-5 border-t-4 shadow-sm grid grid-cols-1 sm:grid-cols-4 gap-6"
        style={{ borderTopColor: "#0072bc" }}
        data-testid="wall-monthly-totals"
      >
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Monthly Revenue</div>
          <div className="font-mono-data font-bold text-[26px] sm:text-[30px] text-[#0072bc] leading-none mt-1" data-testid="wall-monthly-revenue">
            {fmt$c(totals.revenue)}
          </div>
          <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1">{totals.job_count} completed jobs</div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Monthly Goal</div>
          <div className="font-mono-data font-bold text-[26px] sm:text-[30px] text-[#0e1420] leading-none mt-1" data-testid="wall-monthly-goal">
            {totals.monthly_goal != null ? fmt$(totals.monthly_goal) : "—"}
          </div>
          <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1">
            {totals.pct_of_goal != null ? `${totals.pct_of_goal}% of goal` : "no goal set"}
          </div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Close Rate</div>
          <div className="font-mono-data font-bold text-[26px] sm:text-[30px] text-[#0e1420] leading-none mt-1" data-testid="wall-monthly-close-rate">
            {totals.close_rate != null ? `${totals.close_rate}%` : "—"}
          </div>
          <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1">
            {totals.close_rate_weeks > 0
              ? `avg across ${totals.close_rate_weeks} week${totals.close_rate_weeks === 1 ? "" : "s"}`
              : "pending call-center ingest"}
          </div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Weeks Included</div>
          <div className="font-mono-data font-bold text-[26px] sm:text-[30px] text-[#0e1420] leading-none mt-1">
            {data.weeks_included.length}
          </div>
          <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1 truncate">
            {data.weeks_included[0]} → {data.weeks_included[data.weeks_included.length - 1]}
          </div>
        </div>
      </div>

      {/* Top monthly tech */}
      {topTech && (
        <div
          className="bg-white border border-[#e7e9ee] rounded-xl p-5 border-t-4 shadow-sm flex flex-wrap items-center gap-4"
          style={{ borderTopColor: "#FFC72C" }}
          data-testid="wall-monthly-top-tech"
        >
          <div className="w-14 h-14 rounded-xl bg-[#FFC72C] text-[#0e1420] flex items-center justify-center">
            <Trophy size={26} strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Top Monthly Revenue</div>
            <div className="font-display font-bold text-[24px] sm:text-[28px] text-[#0e1420] leading-tight">{topTech.tech_name}</div>
            <div className="text-[12px] text-[color:var(--cc-ink-muted)]">{topTech.unit}</div>
          </div>
          <div className="text-right">
            <div className="font-mono-data font-bold text-[30px] sm:text-[34px] text-[#0072bc] leading-none">{fmt$c(topTech.revenue)}</div>
            <div className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)] mt-1">
              {topTech.pct_of_goal != null ? `${topTech.pct_of_goal}% of ${fmt$(topTech.monthly_goal)}` : "no goal set"}
            </div>
          </div>
        </div>
      )}

      {/* Per-tech table with editable monthly goals */}
      <section data-testid="wall-monthly-tech-goals">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1 h-4 rounded-sm bg-[#0072bc]" />
          <span className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">Monthly Tech Goals & Revenue</span>
          <span className="text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
            · defaults to sum of weekly goals — type to override
          </span>
        </div>
        <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] border-b border-[#eef1f6]">
              <tr>
                <th className="text-left px-4 py-2.5">Tech</th>
                <th className="text-right px-4 py-2.5">Revenue</th>
                <th className="text-right px-4 py-2.5">Monthly Goal</th>
                <th className="text-right px-4 py-2.5">% of Goal</th>
                <th className="text-right px-4 py-2.5 min-w-[150px]">Override</th>
              </tr>
            </thead>
            <tbody>
              {data.techs.map((t) => (
                <tr
                  key={t.tech_name}
                  data-testid={`wall-monthly-tech-${t.tech_name}`}
                  className={`border-b border-[#eef1f6] last:border-b-0 ${t.no_jobs ? "opacity-70" : ""}`}
                >
                  <td className="px-4 py-3">
                    <div className="text-[13px] font-semibold text-[#0e1420]">{t.tech_name}</div>
                    <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">
                      {t.unit || "—"}
                      {t.no_jobs && <span className="ml-1.5 uppercase tracking-widest">· no jobs</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono-data font-bold text-[#0072bc]">
                    {fmt$c(t.revenue)}
                    <div className="text-[10px] font-normal text-[color:var(--cc-ink-muted)] mt-0.5">{t.job_count} jobs</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono-data text-[#0e1420]">
                    {t.monthly_goal != null ? fmt$(t.monthly_goal) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-1">
                      <span className="font-mono-data font-bold text-[#0e1420]">
                        {t.pct_of_goal != null ? `${t.pct_of_goal}%` : "—"}
                      </span>
                      <GoalBadge status={t.goal_status} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <MonthlyGoalInput
                      techName={t.tech_name}
                      monthlyGoal={t.monthly_goal_override ?? t.monthly_goal}
                      derived={t.derived_monthly_goal}
                      hasOverride={t.has_override}
                      month={data.month}
                      canEdit={canEdit}
                      onSaved={() => load(month)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Per-tech monthly revenue chart */}
      <section data-testid="wall-monthly-revenue-chart">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1 h-4 rounded-sm bg-[#0072bc]" />
          <span className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">Monthly Revenue by Tech</span>
        </div>
        <div className="bg-white border border-[#e7e9ee] rounded-xl p-4 space-y-2">
          {data.techs.map((t) => (
            <div key={t.tech_name} className={`grid grid-cols-[130px_1fr_120px] items-center gap-3 ${t.no_jobs ? "opacity-70" : ""}`}>
              <div className="text-[13px] text-[#0e1420] truncate font-semibold">
                {t.tech_name}
                {t.no_jobs && <span className="ml-1.5 text-[9px] font-mono-data uppercase tracking-widest text-[#4b5563]">· no jobs</span>}
              </div>
              <div className="h-[14px] rounded-full bg-[#eef1f6] overflow-hidden">
                <div className="h-full bg-[#0072bc]" style={{ width: `${Math.max(4, (t.revenue / maxRev) * 100)}%` }} />
              </div>
              <div className="font-mono-data font-bold text-right text-[13px] text-[#0072bc]">
                {fmt$c(t.revenue)}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
